/**
 * index.ts
 *
 * NE İŞE YARAR : Metin sözlüğüne erişimin tek kapısı — anahtar çözme ve yer tutucu doldurma
 * KAYNAK SPEC  : 15-arayuz-standardi.md §14 (dil) · §12 (tarih ve saat)
 * BAĞLI OLDUĞU : src/metin/tr.ts · dayjs (tarih biçimlendirme)
 * KULLANAN     : Bütün ekranlar ve bileşenler
 * DİKKAT       : 1) İKİNCİ DİL BU DOSYADAN GİRER (`15` §14). Bugün tek sözlük var;
 *                   İngilizce eklendiğinde değişecek yer burasıdır, ekranlar değil.
 *                   Ekranlar sözlüğü hiç tanımaz, yalnız `metin()`i tanır.
 *                2) EKSİK ANAHTAR SESSİZ KALMAZ. Tip kontrolü zaten yakalar; buna rağmen
 *                   çalışma anında bir anahtar bulunamazsa anahtarın **kendisi** dönür.
 *                   Boş metin dönseydi ekranda boşluk görünür ve sebebi aranırken en son
 *                   bakılan yer burası olurdu.
 *                3) TARİH BİÇİMLENDİRME BURADADIR, EKRANLARDA DEĞİL (`15` §12). Sistem
 *                   UTC saklar, `Europe/Istanbul` gösterir; her ekranın kendi biçimini
 *                   yazması, iki ekranda iki farklı tarih görünmesi demektir.
 *                   **Biçim `15` §12'nin yazdığı biçimdir ve kısaltılamaz:**
 *                   `29 Ağustos 2026 Cumartesi 23:45:12`. 33 karakterdir ve tabloda
 *                   geniş bir sütun kaplar; spec bunu görüp *"kurum tutarlılığı
 *                   okunabilirliğe tercih edildi"* diyerek **kabul etmiştir**. İlk
 *                   yazımda `DD.MM.YYYY HH:mm` kullanılmıştı (2026-08-31 02:58) ve bu
 *                   bir sadeleştirme değil, karara aykırılıktı; 2026-08-31 04:05'te
 *                   düzeltildi.
 *                4) GÖRELİ SÜRE YAZILMAZ (`15` §12) — *"3 gün önce"* denetim izinde
 *                   belirsizlik üretir. Bu yüzden burada göreli biçimlendirici yok ve
 *                   eklenmemelidir.
 *                5) `saniyeyiBicimle` DAKİKA:SANİYE ÜRETİR ve iki haneye tamamlar.
 *                   `4:7` okunduğunda *"dört dakika yedi"* mi *"kırk yedi"* mi olduğu
 *                   anlaşılmaz; sayaçta bu belirsizlik olmaz.
 * SON GÜNCELLEME: 2026-08-31 04:05:00
 */
import dayjs from 'dayjs';

import { TR, type MetinAnahtari } from './tr';

export type { MetinAnahtari };

/** `{ad}` biçimindeki yer tutucuların değerleri. */
export type Yer = Readonly<Record<string, string | number>>;

/**
 * Bir metni anahtarıyla getirir ve yer tutucularını doldurur.
 *
 * ```ts
 * metin('dogrulama.aciklama', { telefon: '+90 5** *** ** 67' })
 * ```
 */
export function metin(anahtar: MetinAnahtari, yer?: Yer): string {
  const ham: string | undefined = TR[anahtar];
  if (ham === undefined) {
    // Başlıktaki not 2 — anahtarın kendisi görünür, boşluk değil.
    return anahtar;
  }
  if (yer === undefined) {
    return ham;
  }
  return ham.replace(/\{(\w+)\}/g, (eslesme, ad: string) => {
    const deger = yer[ad];
    return deger === undefined ? eslesme : String(deger);
  });
}

/**
 * Tarih ve saat — `15` §12: `Europe/Istanbul`, gün.ay.yıl saat:dakika.
 *
 * Saat dilimi `main.tsx`te `dayjs.tz.setDefault` ile kurulur; burada yeniden
 * belirtilmez — iki yerde belirtilseydi biri değiştiğinde diğeri sessizce eski kalırdı.
 */
export function tarihSaat(isoDeger: string | null | undefined): string {
  if (isoDeger === null || isoDeger === undefined || isoDeger === '') {
    return '—';
  }
  return dayjs(isoDeger).format(TARIH_SAAT_BICIMI);
}

/** `15` §12'nin biçimi — kısaltılamaz (başlıktaki not 3). */
const TARIH_SAAT_BICIMI = 'D MMMM YYYY dddd HH:mm:ss';
const TARIH_BICIMI = 'D MMMM YYYY';

/**
 * Yalnız tarih — saat taşımayan alanlar için (`15` §12).
 *
 * Doğum tarihi ve kayıt tarihi gibi alanlarda saat **gösterilmez**: veritabanında da
 * yoktur ve `00:00:00` yazmak, olmayan bir kesinlik uydurmaktır.
 */
export function tarih(isoDeger: string | null | undefined): string {
  if (isoDeger === null || isoDeger === undefined || isoDeger === '') {
    return '—';
  }
  return dayjs(isoDeger).format(TARIH_BICIMI);
}

/**
 * Zaman aralığı — `tstzrange` metnini okunur hâle getirir.
 *
 * **Bu işlev bir kusurun sonucudur.** Yerleşim listesi `valid_period`i sunucudan geldiği
 * gibi basıyordu ve kullanıcı ekranda ham veritabanı metni görüyordu:
 * `["2026-08-31 02:19:53.031935+00",)`. 23. adımda arayüz canlı arka uca karşı
 * koşturulunca görüldü.
 *
 * Üst sınırı olmayan aralık *"süresiz"* diye yazılır — boş bırakılsaydı kullanıcı
 * kaydın bittiğini sanabilirdi.
 */
export function aralik(hamDeger: unknown): string {
  const ham = typeof hamDeger === 'string' ? hamDeger.trim() : '';
  if (ham === '') {
    return '—';
  }

  // `[alt,üst)` — köşeli/parantez sınır kapsayıcılığını gösterir ve kullanıcıyı
  // ilgilendirmez; ayrıştırmada atılır.
  const govde = ham.replace(/^[[(]/, '').replace(/[\])]$/, '');
  const parcalar = govde.split(',');
  if (parcalar.length !== 2) {
    // Beklenmeyen biçim: ham metni basmak yerine bilinmiyor denir.
    return '—';
  }

  const alt = sinir(parcalar[0]);
  const ust = sinir(parcalar[1]);

  if (alt === null && ust === null) {
    return '—';
  }
  if (ust === null) {
    return `${alt ?? '—'} — ${metin('ortak.suresiz')}`;
  }
  return `${alt ?? '—'} — ${ust}`;
}

/** Aralığın tek sınırı — tırnaklar atılır, boş sınır `null` olur. */
function sinir(ham: string | undefined): string | null {
  const temiz = (ham ?? '').trim().replace(/^"|"$/g, '');
  return temiz === '' ? null : tarihSaat(temiz);
}

/** Geri sayım — `dakika:saniye`, iki haneye tamamlanmış (başlıktaki not 5). */
export function saniyeyiBicimle(toplamSaniye: number): string {
  const guvenli = Math.max(0, Math.floor(toplamSaniye));
  const dakika = Math.floor(guvenli / 60);
  const saniye = guvenli % 60;
  return `${String(dakika)}:${String(saniye).padStart(2, '0')}`;
}
